import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import type { Project, ProjectFile, ProjectNote, Session, MiniAppCatalogRow } from '@/api/client';
import { mergeMiniAppLikeCounts, MINI_APPS_CATALOG_QUERY_KEY } from '@/lib/miniAppsCatalogQuery';
import { applyRealtimeSessionUpdate } from '@/lib/sessionRealtimeApply';
import { shouldSkipRealtimeSessionUpdate } from '@/lib/sessionRealtimeDedup';
import { shouldSkipRealtimeProjectUpdate } from '@/lib/projectRealtimeDedup';
import { acceptServerList } from '@/lib/serverListSync';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import {
  invalidateArchivedProjectCaches,
  invalidateReadyMonthProjectLists,
  patchActiveTableListCaches,
  patchScheduleFinalProjectsCache,
  removeProjectFromArchivedCachesOnly,
  removeProjectFromReadyMonthCachesOnly,
  removeProjectFromScheduleFinalCache,
  syncArchivedCachesAfterProjectUpdated,
  syncReadyMonthCachesAfterProjectUpdated,
  syncProjectCollectionsSecondaryCaches,
} from '@/lib/projectListQueryCache';
import { projectEntityQueryKey, removeProjectEntity, setProjectEntity } from '@/lib/projectEntityCache';
import {
  applyTaskChanged,
  projectTasksQueryKey,
  type ProjectTaskChangedPayload,
} from '@/features/newdesign/projects/tasks/taskCache';
import {
  applyBookTextProgress,
  applyGlossaryChanged,
  applyProjectFilesChanged,
  glossaryQueryKey,
  suggestionsQueryKey,
  type GlossaryChangedPayload,
} from '@/features/newdesign/projects/stress/stressCache';
import { createProjectListRefresher, PROJECT_DRAFTS_QUERY_ROOT } from '@/lib/projectFilterRealtime';
import { addDraftId, isDraftDiscarded, removeDraftId } from '@/features/newdesign/projects/drafts/draftModel';
import { isDraftPublishing } from '@/lib/draftPublishing';
import { bookTextProgressSchema, stressEntrySchema } from '@/api/glossaryModel';
import { importedEventSchema, importProgressSchema } from '@/api/glossaryImportModel';
import { applyImported, applyImportProgress } from '@/features/newdesign/projects/stress/import/importCache';
import { applyStressIndexChanged, scheduleStressIndexRefresh } from '@/features/newdesign/projects/stress/viewer/stressIndexCache';
import { invalidateProjectHistory } from '@/features/project-change-history/projectChangeHistoryQuery';
import {
  getLastUndoRedoProjectId,
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';
import { invalidateSessionHistory } from '@/features/session-change-history/sessionChangeHistoryQuery';
import {
  getLastUndoRedoSessionId,
  refreshSessionUndoRedoAvailability,
  setLastUndoRedoSessionId,
} from '@/features/session-change-history/sessionUndoRedoStore';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { patchSessionsProjectFields } from '@/lib/sessionScheduleCache';
import {
  removeSessionFromReaderPlannedCaches,
  upsertReaderPlannedSessionsCache,
} from '@/lib/readerPlannedSessionsCache';
import { READER_PLANNED_COUNTS_ROOT, READER_PLANNED_PAGES_ROOT } from '@/lib/readerPlannedPagesCache';
import {
  accumulateProjectIdsForSessionRowSummaries,
  findCachedSessionProjectId,
  sessionSnapshotFromDeletedRealtimePayload,
  type SessionDeletedRealtimePayload,
} from '@/lib/sessionRowSummariesInvalidation';
import { reconcileProjectsTableFromServer } from '@/lib/projectsTableReconcile';
import {
  markProjectsTableTrustStale,
  markProjectsTableWsConnected,
  markProjectsTableWsDisconnected,
} from '@/lib/projectsTableSyncPolicy';
import {
  ACCOUNT_DEACTIVATED_MESSAGE,
  handleForcedSessionEnd,
  redirectToAuthAndClearSession,
  refreshAccessSessionWhenReachable,
} from '@/lib/authSession';
import { SOCKET_URL } from '@/config/serverConfig';
import { installActiveTabPresence } from '@/lib/activeTabPresence';
import {
  READERS_TABLE_QUERY_KEY,
  READER_TAGS_CATALOG_QUERY_KEY,
  MUSIC_TAGS_CATALOG_QUERY_KEY,
} from '@/lib/readersCatalogQueryCache';
import type { FonotekaPack, FonotekaTrack } from '@/api/client';
import {
  patchFonotekaPackInCache,
  patchFonotekaTrackInCache,
  patchLazyFonotekaCaches,
} from '@/features/fonoteka/cache';
import { receiveProjectFonotekaTracksEvent } from '@/features/fonoteka/projectTracksSync';

let sharedSocket: Socket | null = null;
let hasEverConnected = false;

export function getSocket(): Socket {
  if (!sharedSocket) {
    sharedSocket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      withCredentials: true,
    });
  }
  return sharedSocket;
}

/** После установки httpOnly-куки — новый handshake с тем же сокетом. */
export function reconnectSharedSocket(): void {
  if (!sharedSocket) return;
  if (sharedSocket.connected) {
    sharedSocket.disconnect();
  }
  if (!sharedSocket.connected) {
    sharedSocket.connect();
  }
}

export function disconnectSharedSocket(): void {
  sharedSocket?.disconnect();
}

type ProjectEvent = { type: 'created' | 'updated' | 'deleted'; payload: Project | { id: string } };
type SessionEvent = {
  type: 'created' | 'updated' | 'deleted';
  payload:
    | (Session & { project_name?: string })
    | SessionDeletedRealtimePayload;
};

export function useRealtimeSync(enabled: boolean = true) {
  const queryClient = useQueryClient();
  const projectQueueRef = useRef<ProjectEvent[]>([]);
  const sessionQueueRef = useRef<SessionEvent[]>([]);
  const prevFetchStatusRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!enabled) return;
    const socket = getSocket();
    const uninstallActiveTabPresence = installActiveTabPresence(socket);

    const isProjectsFetching = () =>
      queryClient.getQueryCache().findAll({ queryKey: ['projects', 'table-active-ids'], fetchStatus: 'fetching' })
        .length > 0;
    const isSessionsFetching = () =>
      queryClient.getQueryCache().findAll({ queryKey: ['sessions'], fetchStatus: 'fetching' }).length > 0;

    const projectListRefresher = createProjectListRefresher(queryClient);

    /** Отказ handshake уже вылечен обновлением пропуска — второй подряд значит, что сессии нет. */
    let authRefreshedForHandshake = false;
    let disposed = false;

    const handleConnect = () => {
      authRefreshedForHandshake = false;
      markProjectsTableWsConnected(queryClient);
      if (hasEverConnected) {
        projectListRefresher.schedule();
        // События черновиков за время разрыва потеряны: список черновиков правится точечно, сверка — только здесь
        void queryClient.invalidateQueries({ queryKey: [...PROJECT_DRAFTS_QUERY_ROOT], refetchType: 'active' });
        // «В других проектах»: события сессий за разрыв потеряны — открытые порции и числа сверяются с сервером
        void queryClient.invalidateQueries({ queryKey: [READER_PLANNED_PAGES_ROOT], refetchType: 'active' });
        void queryClient.invalidateQueries({ queryKey: [READER_PLANNED_COUNTS_ROOT], refetchType: 'active' });
        void reconcileProjectsTableFromServer(queryClient, { reason: 'reconnect' }).catch(() => {});
        void queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as unknown[];
            return (
              Array.isArray(key) &&
              key[0] === 'cellComments' &&
              typeof q.getObserversCount === 'function' &&
              q.getObserversCount() > 0
            );
          },
        });
        void queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as unknown[];
            return (
              Array.isArray(key) &&
              key[0] === 'sessionComments' &&
              typeof q.getObserversCount === 'function' &&
              q.getObserversCount() > 0
            );
          },
        });
        void queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as unknown[];
            return (
              Array.isArray(key) &&
              key[0] === 'readers' &&
              typeof q.getObserversCount === 'function' &&
              q.getObserversCount() > 0
            );
          },
        });
        void queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as unknown[];
            return (
              Array.isArray(key) &&
              key[0] === 'readersTableSummaries' &&
              typeof q.getObserversCount === 'function' &&
              q.getObserversCount() > 0
            );
          },
        });
        void queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as unknown[];
            return (
              Array.isArray(key) &&
              (key[0] === 'projectTasks' || key[0] === 'glossary' || key[0] === 'glossarySuggestions' || key[0] === 'glossaryImport' || key[0] === 'stressIndex') &&
              q.getObserversCount() > 0
            );
          },
        });
      }
      hasEverConnected = true;
    };

    const handleDisconnect = () => {
      markProjectsTableWsDisconnected(queryClient);
      markProjectsTableTrustStale(queryClient);
    };

    const handleConnectError = (error: Error) => {
      if (error.message === 'AccountDeactivated') {
        socket.io.opts.reconnection = false;
        disconnectSharedSocket();
        void handleForcedSessionEnd(403, {
          code: 'ACCOUNT_DEACTIVATED',
          error: ACCOUNT_DEACTIVATED_MESSAGE,
        });
        return;
      }
      if (error.message !== 'Unauthorized') return;
      // Пропуск истёк, пока сокет был без связи (сон, смена сети). После отказа middleware socket.io сам
      // не переподключается: обновляем пропуск, дождавшись сервера, и подключаемся заново.
      if (authRefreshedForHandshake) {
        void redirectToAuthAndClearSession();
        return;
      }
      void refreshAccessSessionWhenReachable().then((outcome) => {
        if (disposed) return;
        if (outcome === 'rejected') {
          void redirectToAuthAndClearSession();
          return;
        }
        authRefreshedForHandshake = true;
        socket.connect();
      });
    };

    const handleAccountDeactivated = (payload: unknown) => {
      socket.io.opts.reconnection = false;
      disconnectSharedSocket();
      const body =
        payload && typeof payload === 'object'
          ? payload
          : { code: 'ACCOUNT_DEACTIVATED', error: ACCOUNT_DEACTIVATED_MESSAGE };
      void handleForcedSessionEnd(403, {
        code: 'ACCOUNT_DEACTIVATED',
        error:
          typeof (body as { error?: unknown }).error === 'string' &&
          (body as { error: string }).error.trim()
            ? (body as { error: string }).error.trim()
            : ACCOUNT_DEACTIVATED_MESSAGE,
      });
    };

    const hasExactQuery = (queryKey: readonly unknown[]) => queryClient.getQueryState(queryKey) !== undefined;

    const mergeStableListSnapshot = <T,>(
      prev: T[] | undefined,
      incoming: T[],
      getId: (item: T) => string,
      areEqual: (a: T, b: T) => boolean
    ): T[] => {
      if (!prev) return incoming;

      const prevById = new Map(prev.map((item) => [getId(item), item]));
      let changed = prev.length !== incoming.length;

      const next = incoming.map((item, index) => {
        const prevItem = prevById.get(getId(item));
        if (prevItem && areEqual(prevItem, item)) {
          if (prev[index] !== prevItem) changed = true;
          return prevItem;
        }
        changed = true;
        return item;
      });

      if (!changed) {
        for (let i = 0; i < prev.length; i += 1) {
          if (prev[i] !== next[i]) {
            changed = true;
            break;
          }
        }
      }

      return changed ? next : prev;
    };

    const areProjectNotesEqual = (a: ProjectNote, b: ProjectNote) =>
      a.note_id === b.note_id &&
      a.project_id === b.project_id &&
      a.note_content === b.note_content &&
      a.created_at === b.created_at &&
      a.created_by === b.created_by &&
      a.created_by_name === b.created_by_name &&
      a.display_order === b.display_order;

    const areProjectFilesEqual = (a: ProjectFile, b: ProjectFile) =>
      a.file_id === b.file_id &&
      a.project_id === b.project_id &&
      a.file_name === b.file_name &&
      a.file_url === b.file_url &&
      a.file_size === b.file_size &&
      a.uploaded_at === b.uploaded_at &&
      a.uploaded_by === b.uploaded_by &&
      a.uploaded_by_name === b.uploaded_by_name &&
      a.display_order === b.display_order &&
      (a.file_type ?? null) === (b.file_type ?? null);

    const patchProjectNotesCacheIfOpen = (project: Project) => {
      if (!Array.isArray(project.notes)) return;
      const queryKey = ['projectNotes', project.id] as const;
      if (!hasExactQuery(queryKey)) return;

      queryClient.setQueryData<ProjectNote[]>(queryKey, (prev) =>
        mergeStableListSnapshot(prev, project.notes ?? [], (note) => note.note_id, areProjectNotesEqual)
      );
    };

    const patchProjectFilesCacheIfOpen = (project: Project) => {
      if (!Array.isArray(project.files)) return;
      const queryKey = ['projectFiles', project.id] as const;
      if (!hasExactQuery(queryKey)) return;

      // Снимок сервера с ревизией: устаревший отбрасывается; свои незавершённые правки — поверх (lib/serverListSync)
      const revision = typeof project.files_revision === 'number' ? project.files_revision : null;
      acceptServerList<ProjectFile>(queryClient, queryKey, project.files ?? [], revision, (prev, next) =>
        mergeStableListSnapshot(prev, next, (file) => file.file_id, areProjectFilesEqual)
      );
    };

    const invalidateProjectSessionsPopover = (projectId: string | null | undefined) => {
      if (!projectId) return;
      void queryClient.invalidateQueries({ queryKey: ['projectSessions', projectId] });
    };

    const sortProjectSessions = (sessions: Session[]) =>
      [...sessions].sort((a, b) => {
        const dateDiff = (a.date ?? '').localeCompare(b.date ?? '');
        if (dateDiff !== 0) return dateDiff;
        return (a.start ?? '').localeCompare(b.start ?? '');
      });

    const upsertProjectSessionsPopover = (session: Session) => {
      const targetProjectId = session.project_id;
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['projectSessions'] });

      for (const [queryKey, old] of entries) {
        if (!old) continue;

        const cacheProjectId = queryKey[1];
        const withoutSession = old.filter((item) => item.id !== session.id);

        if (cacheProjectId !== targetProjectId) {
          if (withoutSession.length !== old.length) {
            queryClient.setQueryData<Session[]>(queryKey, withoutSession);
          }
          continue;
        }

        const nextBase = [...withoutSession, session];
        const next = sortProjectSessions(nextBase);
        const unchanged = old.length === next.length && old.every((item, index) => item === next[index]);
        if (!unchanged) {
          queryClient.setQueryData<Session[]>(queryKey, next);
        }
      }

      if (targetProjectId && !hasExactQuery(['projectSessions', targetProjectId])) {
        invalidateProjectSessionsPopover(targetProjectId);
      }
      upsertReaderPlannedSessionsCache(queryClient, session);
    };

    const removeSessionFromProjectPopovers = (sessionId: string) => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['projectSessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const next = old.filter((session) => session.id !== sessionId);
        if (next.length !== old.length) {
          queryClient.setQueryData<Session[]>(queryKey, next);
        }
      }
      removeSessionFromReaderPlannedCaches(queryClient, sessionId);
    };

    const removeProjectSessionsFromAll = (projectId: string) => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const next = old.filter((session) => session.project_id !== projectId);
        if (next.length !== old.length) {
          queryClient.setQueryData<Session[]>(queryKey, next);
        }
      }
    };

    const patchTableActiveAfterCreate = (project: Project) => {
      if (isProjectExcludedFromActiveTable(project.state) || project.is_archived) return;
      patchActiveTableListCaches(queryClient, (prev) => {
        if (!prev) return prev;
        if (prev.some((p) => p.id === project.id)) return prev;
        const withSessions = { ...project, sessions: project.sessions ?? [] };
        const tempIndex = prev.findIndex((p) => p.id.startsWith('temp-'));
        if (tempIndex >= 0) {
          return prev.map((p, i) => (i === tempIndex ? withSessions : p));
        }
        return [withSessions, ...prev];
      });
    };

    const patchTableActiveAfterUpdate = (project: Project) => {
      patchActiveTableListCaches(queryClient, (prev) => {
        if (!prev) return prev;
        const excluded =
          isProjectExcludedFromActiveTable(project.state) || !!project.is_archived;
        const idx = prev.findIndex((p) => p.id === project.id);
        if (excluded) {
          if (idx < 0) return prev;
          return prev.filter((p) => p.id !== project.id);
        }
        const sessions = project.sessions ?? (idx >= 0 ? prev[idx].sessions : undefined) ?? [];
        const merged = { ...project, sessions };
        if (idx >= 0) {
          return prev.map((p) => (p.id === project.id ? merged : p));
        }
        return [merged, ...prev];
      });
    };

    const patchTableActiveAfterDelete = (id: string) => {
      patchActiveTableListCaches(queryClient, (prev) =>
        prev ? prev.filter((p) => p.id !== id) : prev
      );
    };

    const applyProjectCreated = (project: Project) => {
      projectListRefresher.schedule();
      // Черновик видит только автор: он живёт в своей сущности и списке черновиков, но не в таблицах и вторичных кэшах
      if (project.is_draft === true) {
        // Черновик убран здесь (ответ на discard в пути или недавно пришёл): запоздавшее событие его не возвращает
        // Идёт «Создать проект»: эхо ещё черновик и вернуло бы плашку — придёт project:created уже проекта
        if (isDraftDiscarded(project.id) || isDraftPublishing(project.id)) return;
        // Эхо своего черновика, заведённого здесь до ответа сервера: сущность в кэше — источник истины (в ней может быть
        // набранное до ответа), пустой черновик из события её не затирает
        if (!queryClient.getQueryData<Project>(projectEntityQueryKey(project.id))) setProjectEntity(queryClient, project);
        addDraftId(queryClient, project.id);
        return;
      }
      // Публикация черновика (своя другая вкладка): он больше не черновик
      removeDraftId(queryClient, project.id);
      patchTableActiveAfterCreate(project);
      setProjectEntity(queryClient, project);
      patchScheduleFinalProjectsCache(queryClient, project);
      syncProjectCollectionsSecondaryCaches(queryClient);
      invalidateSummariesForProjectIds(queryClient, [project.id]);
      if (isProjectExcludedFromActiveTable(project.state) || project.is_archived) {
        const sync = syncReadyMonthCachesAfterProjectUpdated(queryClient, project);
        if (sync.invalidateSummaries) {
          void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
        }
        invalidateReadyMonthProjectLists(queryClient, sync.readyMonthLists);
        const archSync = syncArchivedCachesAfterProjectUpdated(queryClient, project);
        invalidateArchivedProjectCaches(queryClient, {
          list: archSync.archivedList,
          count: archSync.invalidateArchivedCount,
        });
      }
    };

    const applyProjectUpdated = (project: Project) => {
      // До пропуска эха: своя правка тоже меняет выдачу фильтра
      projectListRefresher.schedule();
      // Колонка «Ударения»: файлы «Текст» и «Глоссарий» — у всех, кто её видит, без перезагрузки. До пропуска эха своей
      // правки: колонка автора показала файлы сразу (showProjectFiles), а страницы и «разбор устарел» сверяет по эху
      if (Array.isArray(project.files)) applyProjectFilesChanged(queryClient, project.id, project.files);
      if (shouldSkipRealtimeProjectUpdate(project.id)) {
        invalidateSummariesForProjectIds(queryClient, [project.id]);
        return;
      }
      if (project.is_draft === true) {
        // Идёт «Создать проект»: эхо ещё черновик и вернуло бы плашку — придёт project:created уже проекта
        if (isDraftDiscarded(project.id) || isDraftPublishing(project.id)) return;
        setProjectEntity(queryClient, project);
        addDraftId(queryClient, project.id);
        return;
      }
      removeDraftId(queryClient, project.id);
      patchTableActiveAfterUpdate(project);
      queryClient.setQueryData<Project>(['project', project.id], project);
      setProjectEntity(queryClient, project);
      patchScheduleFinalProjectsCache(queryClient, project);
      patchSessionsProjectFields(queryClient, project.id, {
        project_name: project.name ?? null,
        project_type: project.project_type ?? null,
      });
      const sync = syncReadyMonthCachesAfterProjectUpdated(queryClient, project);
      if (sync.invalidateSummaries) {
        void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
      }
      invalidateReadyMonthProjectLists(queryClient, sync.readyMonthLists);
      const archSync = syncArchivedCachesAfterProjectUpdated(queryClient, project);
      invalidateArchivedProjectCaches(queryClient, {
        list: archSync.archivedList,
        count: archSync.invalidateArchivedCount,
      });
      patchProjectNotesCacheIfOpen(project);
      patchProjectFilesCacheIfOpen(project);
      invalidateSummariesForProjectIds(queryClient, [project.id]);
      syncProjectCollectionsSecondaryCaches(queryClient);
    };

    const applyProjectDeleted = (payload: { id: string }) => {
      projectListRefresher.schedule();
      setLastUndoRedoProjectId(payload.id);
      void refreshUndoRedoAvailability(queryClient, payload.id).catch(() => {});
      patchTableActiveAfterDelete(payload.id);
      removeProjectEntity(queryClient, payload.id);
      removeDraftId(queryClient, payload.id);
      removeProjectFromReadyMonthCachesOnly(queryClient, payload.id);
      removeProjectFromArchivedCachesOnly(queryClient, payload.id);
      removeProjectFromScheduleFinalCache(queryClient, payload.id);
      removeProjectSessionsFromAll(payload.id);
      invalidateSummariesForProjectIds(queryClient, [payload.id]);
      queryClient.removeQueries({ queryKey: ['projectNotes', payload.id] });
      queryClient.removeQueries({ queryKey: projectTasksQueryKey(payload.id) });
      queryClient.removeQueries({ queryKey: ['projectFiles', payload.id] });
      queryClient.removeQueries({ queryKey: ['projectSessions', payload.id] });
      queryClient.removeQueries({
        predicate: (q) => {
          const key = q.queryKey as unknown[];
          return Array.isArray(key) && key[0] === 'cellComments' && key[1] === payload.id;
        },
      });
      syncProjectCollectionsSecondaryCaches(queryClient);
    };

    const drainProjectQueue = () => {
      const queue = projectQueueRef.current;
      if (queue.length === 0) return;
      projectQueueRef.current = [];
      for (const e of queue) {
        if (e.type === 'created') applyProjectCreated(e.payload as Project);
        else if (e.type === 'updated') applyProjectUpdated(e.payload as Project);
        else if (e.type === 'deleted') applyProjectDeleted(e.payload as { id: string });
      }
    };

    const handleProjectCreated = (project: Project) => {
      if (isProjectsFetching()) {
        projectQueueRef.current.push({ type: 'created', payload: project });
        return;
      }
      applyProjectCreated(project);
    };

    const handleProjectUpdated = (project: Project) => {
      if (isProjectsFetching()) {
        projectQueueRef.current.push({ type: 'updated', payload: project });
        return;
      }
      applyProjectUpdated(project);
    };

    const handleProjectDeleted = (payload: { id: string }) => {
      if (isProjectsFetching()) {
        projectQueueRef.current.push({ type: 'deleted', payload });
        return;
      }
      applyProjectDeleted(payload);
    };

    const handleProjectHistoryUpdated = (payload: { project_id: string }) => {
      if (payload?.project_id) {
        invalidateProjectHistory(queryClient, payload.project_id);
        if (payload.project_id === getLastUndoRedoProjectId()) {
          void refreshUndoRedoAvailability(queryClient, payload.project_id).catch(() => {});
        }
      }
    };

    const handleSessionHistoryUpdated = (payload: { session_id: string }) => {
      if (payload?.session_id) {
        invalidateSessionHistory(queryClient, payload.session_id);
        if (payload.session_id === getLastUndoRedoSessionId()) {
          void refreshSessionUndoRedoAvailability(queryClient, payload.session_id).catch(() => {});
        }
      }
    };

    const updateSessionsInRange = (session: Session, mode: 'add' | 'update') => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const qFrom = queryKey[1] as string | undefined;
        const qTo = queryKey[2] as string | undefined;
        const inRange = qFrom && qTo && session.date >= qFrom && session.date <= qTo;
        if (inRange) {
          const exists = old.some((s) => s.id === session.id);
          if (mode === 'update' && exists) {
            if (shouldSkipRealtimeSessionUpdate(session.id)) continue;
            const cached = old.find((s) => s.id === session.id);
            if (!cached) continue;
            const applied = applyRealtimeSessionUpdate(cached, session);
            if (!applied) continue;
            const next = old.map((s) => (s.id === session.id ? applied : s));
            queryClient.setQueryData<Session[]>(queryKey, next);
          } else if ((mode === 'add' || mode === 'update') && !exists) {
            // Если есть temp-сессия с тем же project/reader/date/start — заменяем, не добавляем.
            // Иначе session:created приходит до HTTP-ответа и получается двойной подсчёт → моргание split.
            const tempMatch = old.find(
              (s) =>
                s.id.startsWith('temp-') &&
                s.project_id === session.project_id &&
                (s.reader_id === session.reader_id ||
                  (s.reader_ids ?? s.readers?.map((r) => r.id) ?? []).some((id) =>
                    (session.reader_ids ?? session.readers?.map((r) => r.id) ?? []).includes(id)
                  )) &&
                (s.date as string)?.slice(0, 10) === (session.date as string)?.slice(0, 10) &&
                (s.start as string)?.slice(0, 5) === (session.start as string)?.slice(0, 5)
            );
            const base = tempMatch ? old.filter((s) => s.id !== tempMatch.id) : old;
            const next = [...base, session].sort((a, b) => {
              const d = a.date.localeCompare(b.date);
              if (d !== 0) return d;
              return (a.start ?? '').slice(0, 5).localeCompare((b.start ?? '').slice(0, 5));
            });
            queryClient.setQueryData<Session[]>(queryKey, next);
          }
        } else if (mode === 'update') {
          const next = old.filter((s) => s.id !== session.id);
          if (next.length !== old.length) queryClient.setQueryData<Session[]>(queryKey, next);
        }
      }
    };

    const removeSessionFromAll = (sessionId: string) => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const next = old.filter((s) => s.id !== sessionId);
        if (next.length !== old.length) queryClient.setQueryData<Session[]>(queryKey, next);
      }
    };

    const drainSessionQueue = () => {
      const queue = sessionQueueRef.current;
      if (queue.length === 0) return;
      sessionQueueRef.current = [];
      const affectedProjectIds = new Set<string>();
      for (const e of queue) {
        if (e.type === 'created') {
          const s = e.payload as Session;
          updateSessionsInRange(s, 'add');
          accumulateProjectIdsForSessionRowSummaries(queryClient, affectedProjectIds, s, {
            kind: 'created',
          });
          upsertProjectSessionsPopover(s);
        } else if (e.type === 'updated') {
          const s = e.payload as Session;
          const prevPid = findCachedSessionProjectId(queryClient, s.id);
          updateSessionsInRange(s, 'update');
          accumulateProjectIdsForSessionRowSummaries(queryClient, affectedProjectIds, s, {
            kind: 'updated',
            previousProjectId: prevPid,
          });
          upsertProjectSessionsPopover(s);
        } else if (e.type === 'deleted') {
          const p = e.payload as SessionDeletedRealtimePayload;
          removeSessionFromAll(p.id);
          accumulateProjectIdsForSessionRowSummaries(
            queryClient,
            affectedProjectIds,
            sessionSnapshotFromDeletedRealtimePayload(p),
            { kind: 'deleted', deletedProjectId: p.project_id ?? null }
          );
          removeSessionFromProjectPopovers(p.id);
          queryClient.removeQueries({ queryKey: ['sessionComments', p.id] });
          void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesRange'] });
          void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesModal'] });
        }
      }
      invalidateSummariesForProjectIds(queryClient, [...affectedProjectIds]);
    };

    const handleSessionCreated = (session: Session & { project_name?: string }) => {
      if (isSessionsFetching()) {
        sessionQueueRef.current.push({ type: 'created', payload: session });
        return;
      }
      updateSessionsInRange(session, 'add');
      upsertProjectSessionsPopover(session);
      const ids = new Set<string>();
      accumulateProjectIdsForSessionRowSummaries(queryClient, ids, session, {
        kind: 'created',
      });
      invalidateSummariesForProjectIds(queryClient, [...ids]);
    };

    const handleSessionUpdated = (session: Session & { project_name?: string }) => {
      if (isSessionsFetching()) {
        sessionQueueRef.current.push({ type: 'updated', payload: session });
        return;
      }
      const prevPid = findCachedSessionProjectId(queryClient, session.id);
      updateSessionsInRange(session, 'update');
      upsertProjectSessionsPopover(session);
      const ids = new Set<string>();
      accumulateProjectIdsForSessionRowSummaries(queryClient, ids, session, {
        kind: 'updated',
        previousProjectId: prevPid,
      });
      invalidateSummariesForProjectIds(queryClient, [...ids]);
    };

    const handleSessionDeleted = (payload: SessionDeletedRealtimePayload) => {
      if (isSessionsFetching()) {
        sessionQueueRef.current.push({ type: 'deleted', payload });
        return;
      }
      setLastUndoRedoSessionId(payload.id);
      void refreshSessionUndoRedoAvailability(queryClient, payload.id).catch(() => {});
      removeSessionFromAll(payload.id);
      removeSessionFromProjectPopovers(payload.id);
      queryClient.removeQueries({ queryKey: ['sessionComments', payload.id] });
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesRange'] });
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesModal'] });
      const ids = new Set<string>();
      accumulateProjectIdsForSessionRowSummaries(
        queryClient,
        ids,
        sessionSnapshotFromDeletedRealtimePayload(payload),
        { kind: 'deleted', deletedProjectId: payload.project_id ?? null }
      );
      invalidateSummariesForProjectIds(queryClient, [...ids]);
      if (payload.project_id && !hasExactQuery(['projectSessions', payload.project_id])) {
        invalidateProjectSessionsPopover(payload.project_id);
      }
    };

    const handleSessionCommentsUpdated = (payload: { sessionId: string }) => {
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesRange'] });
      void queryClient.invalidateQueries({ queryKey: ['sessionCommentSummariesModal'] });
      void queryClient.invalidateQueries({ queryKey: ['sessionComments', payload.sessionId] });
    };

    const handleReadersCatalogChanged = () => {
      void queryClient.refetchQueries({ queryKey: READERS_TABLE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: READER_TAGS_CATALOG_QUERY_KEY });
      void queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'readers' &&
          q.queryKey[1] !== 'table',
      });
      void queryClient.invalidateQueries({ queryKey: ['readersTableSummaries'] });
      void queryClient.invalidateQueries({
        predicate: (q) => {
          const key = q.queryKey as unknown[];
          return (
            Array.isArray(key) &&
            key[0] === 'readerFiles' &&
            typeof q.getObserversCount === 'function' &&
            q.getObserversCount() > 0
          );
        },
      });
    };

    const handleMusicTagsCatalogChanged = () => {
      void queryClient.invalidateQueries({ queryKey: MUSIC_TAGS_CATALOG_QUERY_KEY });
    };

    const handleFonotekaPackChanged = (
      payload: FonotekaPack | { id: string; deleted: true }
    ) => {
      patchFonotekaPackInCache(queryClient, payload);
      patchLazyFonotekaCaches(queryClient, { kind: 'pack', pack: payload });
    };

    const handleFonotekaTrackChanged = (
      payload: FonotekaTrack | { id: string; pack_id: string; deleted: true }
    ) => {
      patchFonotekaTrackInCache(queryClient, payload);
      patchLazyFonotekaCaches(queryClient, { kind: 'track', track: payload });
    };

    const handleProjectFonotekaTracksChanged = (payload: {
      projectId: string;
      track_ids: string[];
    }) => {
      if (!payload?.projectId) return;
      receiveProjectFonotekaTracksEvent(queryClient, payload.projectId, payload.track_ids ?? []);
    };

    const handleProjectTaskChanged = (payload: ProjectTaskChangedPayload) => {
      if (!payload?.projectId) return;
      applyTaskChanged(queryClient, payload);
    };

    const handleGlossaryChanged = (payload: GlossaryChangedPayload) => {
      if (!payload?.projectId) return;
      // Запись из события проверяется той же схемой, что ответ API; не прошла — глоссарий перечитывается
      if ('entry' in payload && !stressEntrySchema.safeParse(payload.entry).success) {
        void queryClient.invalidateQueries({ queryKey: glossaryQueryKey(payload.projectId) });
        return;
      }
      applyGlossaryChanged(queryClient, payload);
      // Знаки в просмотрщике — точечно по записи и её формам
      applyStressIndexChanged(queryClient, payload);
    };
    // Общий словарь: числа «в книге» у каждого проекта свои — открытые глоссарии перечитываются (их один-два);
    // индексы просмотрщика — с ETag, пачка событий — один запрос
    const handleSharedStressChanged = () => {
      void queryClient.invalidateQueries({ queryKey: ['glossary'] });
      void queryClient.invalidateQueries({ queryKey: ['glossarySuggestions'] });
      scheduleStressIndexRefresh(queryClient);
    };
    const handleGlossarySuggestionsChanged = (payload: { projectId: string }) => {
      if (!payload?.projectId) return;
      void queryClient.invalidateQueries({ queryKey: suggestionsQueryKey(payload.projectId) });
    };
    const handleBookTextChanged = (payload: { projectId: string; status: string }) => {
      if (!payload?.projectId) return;
      void queryClient.invalidateQueries({ queryKey: glossaryQueryKey(payload.projectId) });
      void queryClient.invalidateQueries({ queryKey: suggestionsQueryKey(payload.projectId) });
    };
    // Ход разбора — точечно в открытый глоссарий; событие не по контракту пропускается (следующее или итог поправят)
    const handleBookTextProgress = (payload: unknown) => {
      const parsed = bookTextProgressSchema.safeParse(payload);
      if (parsed.success) applyBookTextProgress(queryClient, parsed.data);
    };

    // Загрузка глоссария: ход — точечно в окно; применение или отмена — один перечёт глоссария (сотни записей разом)
    const handleGlossaryImportProgress = (payload: unknown) => {
      const parsed = importProgressSchema.safeParse(payload);
      if (parsed.success) applyImportProgress(queryClient, parsed.data);
    };
    const handleGlossaryImported = (payload: unknown) => {
      const parsed = importedEventSchema.safeParse(payload);
      if (parsed.success) {
        applyImported(queryClient, parsed.data);
        // Знаки в просмотрщике: загрузка тронула общий словарь — у всех открытых проектов, иначе — у своего
        if (parsed.data.shared) scheduleStressIndexRefresh(queryClient);
        else if (parsed.data.projectId) scheduleStressIndexRefresh(queryClient, parsed.data.projectId);
      }
    };

    const handleMiniAppsLikesUpdated = (payload: { items: { id: string; likes_count: number }[] }) => {
      queryClient.setQueryData<MiniAppCatalogRow[] | undefined>(MINI_APPS_CATALOG_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return mergeMiniAppLikeCounts(prev, payload.items);
      });
    };

    const handleCacheUpdate = (event: { type: string; query?: { queryKey: unknown[]; state: { fetchStatus: string } } }) => {
      if (event.type !== 'updated' || !event.query) return;
      const key = event.query.queryKey as string[];
      const qk = key[0];
      const fetchStatus = event.query.state.fetchStatus;
      const prev = prevFetchStatusRef.current.get(JSON.stringify(key));
      prevFetchStatusRef.current.set(JSON.stringify(key), fetchStatus);
      if (prev === 'fetching' && fetchStatus === 'idle') {
        if (qk === 'projects') drainProjectQueue();
        else if (qk === 'sessions') drainSessionQueue();
      }
    };

    const unsubCache = queryClient.getQueryCache().subscribe(handleCacheUpdate);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('auth:account_deactivated', handleAccountDeactivated);
    socket.on('project:created', handleProjectCreated);
    socket.on('project:updated', handleProjectUpdated);
    socket.on('project:deleted', handleProjectDeleted);
    socket.on('project:history-updated', handleProjectHistoryUpdated);
    socket.on('session:history-updated', handleSessionHistoryUpdated);
    socket.on('session:created', handleSessionCreated);
    socket.on('session:updated', handleSessionUpdated);
    socket.on('session:deleted', handleSessionDeleted);
    socket.on('session:commentsUpdated', handleSessionCommentsUpdated);
    socket.on('readers:catalogChanged', handleReadersCatalogChanged);
    socket.on('musicTags:catalogChanged', handleMusicTagsCatalogChanged);
    socket.on('fonoteka:packChanged', handleFonotekaPackChanged);
    socket.on('fonoteka:trackChanged', handleFonotekaTrackChanged);
    socket.on('project:fonotekaTracksChanged', handleProjectFonotekaTracksChanged);
    socket.on('project:taskChanged', handleProjectTaskChanged);
    socket.on('project:glossaryChanged', handleGlossaryChanged);
    socket.on('stress:sharedChanged', handleSharedStressChanged);
    socket.on('project:glossarySuggestionsChanged', handleGlossarySuggestionsChanged);
    socket.on('project:bookTextChanged', handleBookTextChanged);
    socket.on('project:bookTextProgress', handleBookTextProgress);
    socket.on('project:glossaryImportProgress', handleGlossaryImportProgress);
    socket.on('project:glossaryImported', handleGlossaryImported);
    socket.on('mini_apps:likes_updated', handleMiniAppsLikesUpdated);

    return () => {
      disposed = true;
      projectListRefresher.dispose();
      uninstallActiveTabPresence();
      unsubCache();
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('auth:account_deactivated', handleAccountDeactivated);
      socket.off('project:created', handleProjectCreated);
      socket.off('project:updated', handleProjectUpdated);
      socket.off('project:deleted', handleProjectDeleted);
      socket.off('project:history-updated', handleProjectHistoryUpdated);
      socket.off('session:history-updated', handleSessionHistoryUpdated);
      socket.off('session:created', handleSessionCreated);
      socket.off('session:updated', handleSessionUpdated);
      socket.off('session:deleted', handleSessionDeleted);
      socket.off('session:commentsUpdated', handleSessionCommentsUpdated);
      socket.off('readers:catalogChanged', handleReadersCatalogChanged);
      socket.off('musicTags:catalogChanged', handleMusicTagsCatalogChanged);
      socket.off('fonoteka:packChanged', handleFonotekaPackChanged);
      socket.off('fonoteka:trackChanged', handleFonotekaTrackChanged);
      socket.off('project:fonotekaTracksChanged', handleProjectFonotekaTracksChanged);
      socket.off('project:taskChanged', handleProjectTaskChanged);
      socket.off('project:glossaryChanged', handleGlossaryChanged);
      socket.off('stress:sharedChanged', handleSharedStressChanged);
      socket.off('project:glossarySuggestionsChanged', handleGlossarySuggestionsChanged);
      socket.off('project:bookTextChanged', handleBookTextChanged);
      socket.off('project:bookTextProgress', handleBookTextProgress);
      socket.off('project:glossaryImportProgress', handleGlossaryImportProgress);
      socket.off('project:glossaryImported', handleGlossaryImported);
      socket.off('mini_apps:likes_updated', handleMiniAppsLikesUpdated);
    };
  }, [queryClient, enabled]);
}
